import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, NavigationCancel, NavigationEnd, NavigationError, NavigationStart, Router, Event } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { Subscription } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { CacheService } from '@core/services/cache.service';
import { LayoutService } from '@core/services/layout.service';
import { LoadingService } from '@core/services/loading.service';
import { NavigationService } from '@core/services/navigation.service';
import { NgSelectConfig } from '@ng-select/ng-select';
import { VersionCheckService } from '@core/services/version-check.service';
import { config } from '@configs/config';

@Component({
    standalone: false,
    selector: 'noa-root',
    template: `
        <mat-progress-bar
            *ngIf="showProgressBar"
            mode="indeterminate"
            color="warn"
            class="app-progress-bar"
        ></mat-progress-bar>
        <div class="settings__squaredBorders">
            <router-outlet></router-outlet>
        </div>
    `,
})
export class AppComponent implements OnInit, OnDestroy {
    showProgressBar = false;
    private isRouterLoading = false;
    private isHttpLoading = false;
    private subscriptions = new Subscription();

    constructor(
        private router: Router,
        private activatedRoute: ActivatedRoute,
        private titleService: Title,
        private translate: TranslateService,
        private cacheService: CacheService,
        private layoutService: LayoutService,
        private loadingService: LoadingService,
        private navigationService: NavigationService,
        private ngSelectConfig: NgSelectConfig,
        private versionCheckService: VersionCheckService
    ) {
        this.setTranslationConfig();
    }

    ngOnInit() {
        let routerEvent: Event;
        // check version
        this.versionCheckService.initVersionCheck('./version.json');

        // set page title from router data variable
        this.subscriptions.add(
            this.router.events
                .pipe(
                    filter(event => {
                        routerEvent = event;
                        return event instanceof NavigationEnd;
                    }),
                    map(() => this.activatedRoute),
                    map(route => {
                        while (route.firstChild) {
                            route = route.firstChild;
                        }
                        return route;
                    }),
                    filter(route => route.outlet === 'primary')
                )
                .subscribe((route: ActivatedRoute) => {
                    const data = route.snapshot.data;
                    this.layoutService.changeScrollToValue(0);
                    this.cacheService.registerContext(data, routerEvent);
                    this.navigationService.route = route;
                    this.titleService.setTitle(config.title + (data.title ? ` | ${this.translate.instant(data.title)}` : ''));
                })
        );

        this.subscriptions.add(
            this.router.events.subscribe(event => {
                if (event instanceof NavigationStart) {
                    this.isRouterLoading = true;
                } else if (
                    event instanceof NavigationEnd ||
                    event instanceof NavigationCancel ||
                    event instanceof NavigationError
                ) {
                    this.isRouterLoading = false;
                }
                this.updateProgressBar();
            })
        );

        this.subscriptions.add(
            this.loadingService.isSpinning$.subscribe(isSpinning => {
                this.isHttpLoading = isSpinning;
                this.updateProgressBar();
            })
        );
    }

    ngOnDestroy() {
        this.subscriptions.unsubscribe();
    }

    private updateProgressBar() {
        this.showProgressBar = this.isRouterLoading || this.isHttpLoading;
    }

    setTranslationConfig() {
        // this language will be used as a fallback when a translation isn't found in the current language
        this.translate.setDefaultLang('es');

        // the lang to use, if the lang isn't available, it will use the current loader to get them
        this.translate.use('es');

        // ng-select translation config
        this.translate.get(['messages.no_results', 'messages.type_to_search', 'messages.loading']).subscribe(value => {
            this.ngSelectConfig.notFoundText = value['messages.no_results'];
            this.ngSelectConfig.typeToSearchText = value['messages.type_to_search'];
            this.ngSelectConfig.loadingText = value['messages.loading'];
        });
    }
}
